import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { authClient } from '../lib/auth-client.js';
import { Note, Problem, button } from '../components/form.js';

type Session = {
  id: string;
  token: string;
  createdAt: string | Date;
  /**
   * When Better Auth last REFRESHED this session, which is the closest thing to
   * "last used" that exists — see `lastUsedPhrase` for why it is only ever
   * approximate.
   */
  updatedAt?: string | Date | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  /**
   * Did the holder say this sign-in was on a shared or public device?
   * (auth#419)
   *
   * THREE STATES, and the optionality is not laziness. `true` and `false` are
   * answers somebody gave on the sign-in form; `null` or absent means nobody
   * was asked — a social or passkey sign-in, an admin impersonation, or a
   * session that predates the column. Absent also covers an auth deployment
   * older than the field, which is why this is optional rather than merely
   * nullable.
   *
   * Only `true` renders anything. See the marker below for why.
   */
  sharedDevice?: boolean | null;
};

/**
 * Where this account is signed in, and how to end it.
 *
 * ── The copy here was WRONG and is not ported ────────────────────────
 *
 * The old portal said: "Revoking a session ends single-sign-on on that device.
 * Individual apps stay signed in until their session expires." That was true
 * when written and is now false — `revokeAllGrants` (auth#6) routes every
 * credential-affecting event through one helper that clears sessions AND the
 * OAuth tokens issued from them.
 *
 * auth#10 flags this explicitly as a thing to fix rather than carry across. The
 * difference matters at exactly the wrong moment: somebody revoking a session
 * from a lost laptop needs to know whether the apps on it are still signed in.
 * The old wording says yes. The truth is no.
 */
export function Sessions({ currentToken }: { currentToken?: string }) {
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    const { data, error: err } = await authClient.listSessions();
    if (err) {
      setError(err.message ?? 'Could not load your sessions.');
      return;
    }
    setError(null);
    /**
     * `Array.isArray`, not a cast.
     *
     * `(data ?? []) as T[]` is a lie the compiler cannot check: a proxy error, an
     * HTML fallback or a changed response shape all arrive as SOMETHING, the cast
     * accepts it, and `.map` then throws — taking down the whole Security screen,
     * because these sections are now siblings on one page rather than separate
     * tabs. Consolidating raised the cost of this exact failure.
     *
     * Found by previewing: `TypeError: sessions.map is not a function`. Same
     * family as RidgeWill/account#16 and RidgeWill/auth#150 — a response that is
     * not what it claims, trusted because the type said so.
     */
    if (!Array.isArray(data)) {
      setError('Could not load your sessions.');
      return;
    }
    setSessions(data as Session[]);
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(token: string) {
    setBusy(token);
    const { error: err } = await authClient.revokeSession({ token });
    setBusy(null);
    if (err) {
      setError(err.message ?? 'That session could not be ended.');
      return;
    }
    await load();
  }

  if (error && !sessions) return <Problem>{error}</Problem>;
  if (!sessions) return <Note>Loading…</Note>;

  return (
    <>
      <Note>
        Ending a session signs that device out <strong>and</strong> revokes the access it gave to
        Ridgewill apps. They will not stay signed in.
      </Note>

      {error && <Problem>{error}</Problem>}

      <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0 0' }}>
        {sessions.map((s) => {
          const isCurrent = Boolean(currentToken) && s.token === currentToken;
          return (
            <li
              key={s.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                padding: '.85rem 0',
                borderTop: `1px solid ${C.inputBorder}`,
              }}
            >
              <div>
                <div style={{ fontSize: '.95rem' }}>
                  {describe(s.userAgent)}
                  {isCurrent && (
                    <span style={{ marginLeft: '.5rem', color: C.brand, fontSize: '.8rem' }}>
                      this device
                    </span>
                  )}
                  {/*
                    ONLY `=== true` RENDERS, and the other two states are
                    deliberately silent (auth#419).

                    `false` needs no badge: an ordinary session is what every
                    row here already looks like, and labelling it "private
                    device" would be a reassurance the portal cannot give — we
                    know what somebody TICKED, not where they were sitting.

                    `null` is the one that must stay quiet. It means nobody was
                    asked, which covers social and passkey sign-ins, admin
                    impersonation, and every session created before the field
                    existed. Rendering anything there would turn "we do not
                    know" into a claim.

                    So this badge is one-directional on purpose: its presence is
                    information, its absence is not.
                  */}
                  {s.sharedDevice === true && (
                    <span
                      style={{
                        marginLeft: '.5rem',
                        padding: '.1rem .4rem',
                        borderRadius: '4px',
                        background: C.warnBg,
                        color: C.warnText,
                        fontSize: '.8rem',
                      }}
                    >
                      shared device
                    </span>
                  )}
                </div>
                <Note>
                  {s.ipAddress ? `${s.ipAddress} · ` : ''}
                  since {new Date(s.createdAt).toLocaleString()}
                  {(() => {
                    // Hidden on the current device: "this device" already says
                    // it is in use, and "last used today" under it is noise.
                    if (isCurrent) return null;
                    const phrase = lastUsedPhrase(s.createdAt, s.updatedAt);
                    return phrase ? ` · last used ${phrase}` : null;
                  })()}
                </Note>
              </div>
              {/*
                The CURRENT session has no button. Ending it from here would sign
                somebody out mid-task with no confirmation and no explanation —
                and "sign out" already exists, says what it does, and is where
                somebody looks for it.
              */}
              {!isCurrent && (
                <button
                  type="button"
                  disabled={busy === s.token}
                  onClick={() => void revoke(s.token)}
                  style={{ ...button, marginTop: 0, whiteSpace: 'nowrap' }}
                >
                  {busy === s.token ? 'Ending…' : 'End session'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * "Last used", in the only precision this value actually has.
 *
 * ── There is no last-used field, and there should not be ────────────
 *
 * Jon's call, 2026-08-25 (RidgeWill/auth#154). Passkeys got a real `lastUsedAt`
 * column because nothing recorded their use at all. Sessions are different:
 * `updatedAt` already moves whenever Better Auth refreshes one, so the
 * information exists and this screen simply was not showing it.
 *
 * What it does NOT have is precision. Better Auth refreshes at most once per
 * `session.updateAge`, and auth sets no `session` block, so the default of ONE
 * DAY applies. `updatedAt` is therefore last-used rounded to the day, and this
 * function exists to make sure the screen never implies otherwise. A timestamp
 * here would be a portal asserting a precision the value does not have.
 *
 * Making it exact would mean a database write on EVERY authenticated request,
 * which is the thing `updateAge` exists to prevent. If the granularity is ever
 * too coarse, the fix is to lower `session.updateAge` in auth — one line, no
 * migration — not a new column here.
 *
 * ── Null means "do not show this" ───────────────────────────────────
 *
 * A session created today has `updatedAt` equal or near-equal to `createdAt`,
 * and "since 25 Aug, 21:14 · last used today" tells a reader nothing the first
 * half did not. So this returns null while the two fall on the same calendar
 * day, and the line stays short for the common case.
 */
export function lastUsedPhrase(
  createdAt: string | Date,
  updatedAt: string | Date | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!updatedAt) return null;

  const used = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  const made = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(used.getTime()) || Number.isNaN(made.getTime())) return null;

  // Same calendar day as it was created: the "since" half already said this.
  if (used.toDateString() === made.toDateString()) return null;

  const today = new Date(now);
  if (used.toDateString() === today.toDateString()) return 'today';

  const yesterday = new Date(now - DAY);
  if (used.toDateString() === yesterday.toDateString()) return 'yesterday';

  /**
   * Whole days, from the elapsed time rather than from a calendar difference.
   * Deliberately coarse past a week: the question this answers is "is that old
   * tablet still signed in", and "3 weeks ago" answers it as well as a date
   * while being harder to mistake for a precise reading.
   */
  const days = Math.floor((now - used.getTime()) / DAY);
  if (days < 0) return null; // Clock skew. Silence beats "in 2 days".
  if (days < 7) return `${days} days ago`;
  if (days < 31) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * A user agent, as something a person recognises.
 *
 * Deliberately coarse. The goal is "is that me?", answered from the device in
 * front of them — not an inventory. A full UA string is unreadable and a precise
 * parser is a dependency and a maintenance burden for a line of text.
 */
export function describe(ua?: string | null): string {
  if (!ua) return 'Unknown device';
  const os = /Windows/i.test(ua)
    ? 'Windows'
    : /iPhone|iPad|iOS/i.test(ua)
      ? 'iOS'
      : /Android/i.test(ua)
        ? 'Android'
        : /Mac OS X|Macintosh/i.test(ua)
          ? 'macOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : null;
  // Edge and Chrome both claim Chrome, and Safari claims it too — so the
  // specific ones are tested before the generic.
  const browser = /Edg\//i.test(ua)
    ? 'Edge'
    : /OPR\//i.test(ua)
      ? 'Opera'
      : /Firefox\//i.test(ua)
        ? 'Firefox'
        : /Chrome\//i.test(ua)
          ? 'Chrome'
          : /Safari\//i.test(ua)
            ? 'Safari'
            : null;
  if (browser && os) return `${browser} on ${os}`;
  return browser ?? os ?? 'Unknown device';
}
